'use client';

import { c as _c } from "react/compiler-runtime";
import { Clipboard, ClipboardCheck, Gift } from '@byway/icons';
import { parseISO } from 'date-fns';
import { useFormatter, useTranslations } from 'next-intl';
import pluralize from 'pluralize';
import { useRef, useState } from 'react';
import { Button } from '~/components/Button';
import { Card } from '~/components/Card/src/Card';
import { useToasts } from '~/components/Toast/src/Toast';
import { InfoPopover } from '~/components/Tooltip/src/InfoPopover';
import type { Credit } from '~/services/referral';
import { api } from '~/trpc/react-client';
import styles from './CreditsDisplay.module.css';
import { MergeCreditsDialog } from './MergeCreditsDialog';
interface CreditsDisplayProps {
  readonly credits: readonly Credit[];
}
export function CreditsDisplay(t0) {
  const $ = _c(29);
  const {
    credits: initialCredits
  } = t0;
  const t = useTranslations("account");
  const format = useFormatter();
  let t1;
  if ($[0] !== format) {
    t1 = amount => format.number(amount, {
      style: "currency",
      currency: "GBP",
      maximumFractionDigits: 0
    });
    $[0] = format;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const formatGBP = t1;
  let t2;
  if ($[2] !== format) {
    t2 = dateString => format.dateTime(parseISO(dateString), {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit"
    });
    $[2] = format;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const formatDate = t2;
  let t3;
  if ($[4] !== formatDate) {
    t3 = expiring => expiring.length === 0 ? "" : formatDate(expiring.reduce(_temp, expiring[0].expiresAt));
    $[4] = formatDate;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const getEarliestExpiryDate = t3;
  const tCommon = useTranslations("common");
  const toasts = useToasts();
  const utils = api.useUtils();
  let t4;
  if ($[6] !== initialCredits) {
    t4 = Array.from(initialCredits || []);
    $[6] = initialCredits;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const [credits, setCredits] = useState(t4);
  const [prevInitialCredits, setPrevInitialCredits] = useState(initialCredits);
  const [copiedCode, setCopiedCode] = useState(null);
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  const [mergeError, setMergeError] = useState(false);
  const [showFullIntro, setShowFullIntro] = useState(false);
  const mergedCodeRef = useRef(null);
  if (initialCredits !== prevInitialCredits) {
    setPrevInitialCredits(initialCredits);
    setCredits(Array.from(initialCredits || []));
  }
  const activeCredits = (credits || []).filter(_temp2);
  const totalCredit = activeCredits.reduce(_temp3, 0);
  let t5;
  if ($[8] !== utils) {
    t5 = data => {
      setCredits([data]);
      utils.referral.getCodeAndCredits.invalidate();
      setShowMergeDialog(false);
      setMergeError(false);
      setTimeout(() => {
        mergedCodeRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center"
        });
      }, 100);
    };
    $[8] = utils;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setShowMergeDialog(false);
      setMergeError(true);
    };
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t5) {
    t7 = {
      onSuccess: t5,
      onError: t6
    };
    $[11] = t5;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const mergeMutation = api.referral.mergeCredits.useMutation(t7);
  let t8;
  if ($[13] !== t || $[14] !== toasts) {
    t8 = async code => {
      try {
        await navigator.clipboard.writeText(code);
        setCopiedCode(code);
        setTimeout(() => setCopiedCode(null), 5000);
        toasts.add({
          title: t("credit_code_copy_toast_title"),
          description: t("credit_code_copy_toast_description"),
          variant: "success",
          position: "middle"
        }, {
          timeout: 3000
        });
      } catch {}
    };
    $[13] = t;
    $[14] = toasts;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const handleCopyCode = t8;
  let t9;
  if ($[16] !== mergeMutation) {
    t9 = () => {
      mergeMutation.mutate();
    };
    $[16] = mergeMutation;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const handleMergeConfirm = t9;
  const T0 = Card;
  const t10 = "greyTertiary";
  const t11 = "--by-border-radius-400";
  const t12 = styles;
  let t13;
  if ($[18] !== t) {
    t13 = t("credit_heading");
    $[18] = t;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== t13) {
    t14 = <div className={styles.header}><h2 className={styles.heading}>{t13}</h2></div>;
    $[20] = t13;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  const t15 = activeCredits.length > 0 ? <><div className={styles.creditSummary}><div className={styles.creditSummaryIcon}><Gift decorative={true} size="scale" /></div><div className={styles.creditSummaryHeader}><p className={styles.creditLabel}>{t("credit_total", {
            count: activeCredits.length,
            referrals: pluralize("referral", activeCredits.length)
          })}</p><p className={styles.creditAmount}>{formatGBP(totalCredit)}</p></div></div><div className={styles.intro}><p className={styles.introText}>{showFullIntro ? `${t("credit_intro_short")} ${t("credit_intro_extended")}` : `${t("credit_intro_short")}...`}</p><button type="button" className={styles.readMoreToggle} onClick={() => setShowFullIntro(_temp4)} aria-expanded={showFullIntro}>{showFullIntro ? tCommon("read_less") : tCommon("read_more")}</button></div><hr className={styles.divider} /><div className={styles.columnHeaders}><span>{t("credit_expiry_label")}</span><span>{t("credit_code_column_header")}</span></div><div className={styles.creditsList}>{activeCredits.map((credit, index) => <div key={credit.couponCode} ref={index === 0 ? mergedCodeRef : undefined} className={styles.creditItem}><div className={styles.creditInfo}><Gift decorative={true} className={styles.creditIcon} size="scale" /><div className={styles.creditDetails}><span className={styles.creditItemAmount}>{formatGBP(credit.amount)}</span><InfoPopover ariaLabel={t("credit_price_tooltip_aria")} placement="bottom"><p className={styles.tooltipBody}>{t("credit_price_tooltip")}</p></InfoPopover><span className={styles.creditItemExpiry}>{formatDate(credit.expiresAt)}</span></div></div>{credit.isStripeCoupon && <button type="button" className={styles.codeLink} onClick={() => handleCopyCode(credit.couponCode)} data-test-id="credit-copy-button" aria-label={`${t("referral_copy_button")}: ${credit.couponCode}`}>{copiedCode === credit.couponCode ? <ClipboardCheck decorative={true} size="small" /> : <Clipboard decorative={true} size="small" />}<span>{credit.couponCode}</span></button>}</div>)}</div>{mergeError && <p className={styles.mergeError} role="alert">{t("credit_merge_error")}</p>}{activeCredits.length > 1 && <div className={styles.redeemButtonWrapper}><Button onClick={() => {
        setShowMergeDialog(true);
        setMergeError(false);
      }} data-test-id="merge-credits-button">{t("credit_redeem_button")}</Button></div>}<MergeCreditsDialog isOpen={showMergeDialog} onOpenChange={setShowMergeDialog} onConfirm={handleMergeConfirm} isLoading={mergeMutation.isPending} expiryDate={getEarliestExpiryDate(activeCredits)} /></> : <p className={styles.emptyState}>{t("credit_empty_state")}</p>;
  let t16;
  if ($[22] !== t12.cardWrapper || $[23] !== t14 || $[24] !== t15) {
    t16 = <div className={t12.cardWrapper}>{t14}{t15}</div>;
    $[22] = t12.cardWrapper;
    $[23] = t14;
    $[24] = t15;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] !== T0 || $[27] !== t16) {
    t17 = <T0 colour={t10} rounded={t11}>{t16}</T0>;
    $[26] = T0;
    $[27] = t16;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  return t17;
}
function _temp4(prev) {
  return !prev;
}
function _temp3(sum, c_1) {
  return sum + c_1.amount;
}
function _temp2(c_0) {
  return c_0.status === "ACTIVE";
}
function _temp(min, c) {
  return c.expiresAt < min ? c.expiresAt : min;
}